"use client";

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import AppWrapper from "@/shared/ui/components/app-wrapper";
import Button from "@/shared/ui/components/button";
import Banner, { BannerType } from "@/shared/ui/components/banner";
import LoadingPage from "@/shared/ui/components/loading-page";
import MoneyAmount from "@/shared/ui/components/money-amount";
import ApplePayModal from "@/shared/ui/components/apple-pay-modal";
import { SuccessFilledIcon } from "@/shared/ui/icons/success-filled-icon";
import { withCheckoutId } from "@/lib/checkout-id";
import { parsePaymentResult as parsePaymentResultLib } from "@/lib/payment-result";
import {
  handleHttpError,
  type ApiError,
} from "@/lib/http-error-recovery";
import { CksCheckoutStateSchema } from "@/services/cosmos/schema/checkout-service";
import i18n from "@/i18n";
import enUSErrors from "@/locale/en_US.errors.json";
import arSAErrors from "@/locale/ar_SA.errors.json";
import {
  type AppContext,
  type CheckoutSession,
  CHECKOUT_STATUS_PROCESSING_PAYMENT,
  CheckoutStatus,
} from "@/types";
import {
  getRepaymentFlowContext,
  isOverdueRepaymentFlow,
  type RepaymentFlowContext,
} from "@/modules/early-repayment/lib/repayment-flow";
import {
  clearLatchedOverdueRepaymentFlow,
  latchOverdueRepaymentFlow,
} from "@/modules/early-repayment/lib/overdue-flow-latch";
import { resolveRepaymentDaysOverdue } from "@/modules/early-repayment/lib/resolve-days-overdue";
import {
  useCheckoutSessionV3,
  useSavedCards,
  useSelectedPaymentMethod,
  usePaymentPlansActions,
} from "@/modules/checkout-session";
import { useCosmosClient } from "@/services/cosmos";
import { payWithApple } from "@/modules/payment-methods/services/payment-submission-service";
import { PaymentMethodList } from "@/modules/payment-methods/components/payment-method-list";
import { InlineCardForm } from "@/modules/payment-methods/components/inline-card-form";
import { CvvModal } from "@/modules/payment-methods/components/cvv-modal";
import {
  PageError,
  Processing,
  type PaymentMethodOption,
} from "@/modules/payment-methods/types";
import { usePaymentMethods } from "@/modules/payment-methods/hooks/use-payment-methods";
import { usePaymentSubmission } from "@/modules/payment-methods/hooks/use-payment-submission";
import { usePaymentPolling } from "@/modules/payment-methods/hooks/use-payment-polling";
import { usePaymentModals } from "@/modules/payment-methods/hooks/use-payment-modals";
import { use3dsFailureReporting } from "@/modules/payment-methods/hooks/use-3ds-failure-reporting";
import { useSlowPaymentTracking } from "@/modules/payment-methods/hooks/use-slow-payment-tracking";
import { usePaymentMethodsTracking } from "@/modules/payment-methods/hooks/use-payment-methods-tracking";
import {
  emitCardFormTracking,
  emitModalTracking,
  emitOverduePaymentsTracking,
  overduePaymentsTotalPayload,
} from "@/shared/tracking";
import { ChevronDown, ChevronUp } from "lucide-react";
import * as events from "@/events";
import { logError } from "@/services/logging/logger";
import CircularProgress from "@mui/material/CircularProgress";

type PaymentErrorContent = {
  message: string;
};

type PaymentErrorLocale = {
  payment: Record<string, PaymentErrorContent>;
};

const paymentErrorLocales: Record<string, PaymentErrorLocale> = {
  en_US: enUSErrors as PaymentErrorLocale,
  ar_SA: arSAErrors as PaymentErrorLocale,
};

const getPaymentErrorMessage = (code: string | undefined): string => {
  const language = i18n.language?.startsWith("ar") ? "ar_SA" : "en_US";
  const locale = paymentErrorLocales[language] ?? paymentErrorLocales.en_US;
  const paymentErrors = locale.payment;

  if (code && paymentErrors[code]) {
    return paymentErrors[code].message;
  }

  return paymentErrors.UNKNOWN.message;
};

interface EarlyRepaymentScreenProps {
  onSessionFetched: (session: CheckoutSession) => void;
}

interface CardFormState {
  cardNumber: string;
  expiryDate: string;
  cvv: string;
  cardholderName: string;
  saveCard: boolean;
}

const EMPTY_CARD_FORM: CardFormState = {
  cardNumber: "",
  expiryDate: "",
  cvv: "",
  cardholderName: "",
  saveCard: false,
};

/** Receipt icon used inside the warning alert (matches Figma 314:8363). */
function ReceiptIcon() {
  return (
    <svg width="18" height="20" viewBox="0 0 18 20" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M2.65 19.3155C1.91917 19.3155 1.29483 19.0565 0.777 18.5385C0.259 18.0207 0 17.3993 0 16.6745V13.8155H3.05V0L4.46925 1.03475L5.927 0L7.3845 1.03475L8.84225 0L10.3 1.03475L11.7578 0L13.2155 1.03475L14.673 0L16.1308 1.03475L17.55 0V16.6655C17.55 17.3963 17.291 18.0207 16.773 18.5385C16.2552 19.0565 15.6308 19.3155 14.9 19.3155H2.65ZM14.9 17.6655C15.1833 17.6655 15.4208 17.5697 15.6125 17.378C15.8042 17.1863 15.9 16.9488 15.9 16.6655V2.3655H4.7V13.8155H13.9V16.6655C13.9 16.9488 13.9958 17.1863 14.1875 17.378C14.3792 17.5697 14.6167 17.6655 14.9 17.6655ZM5.8845 6.2655V4.6155H11.5308V6.2655H5.8845ZM5.8845 9.7155V8.0655H11.5308V9.7155H5.8845ZM13.6095 6.53475C13.3198 6.53475 13.0651 6.42483 12.8453 6.205C12.6254 5.985 12.5155 5.73017 12.5155 5.4405C12.5155 5.15083 12.6254 4.896 12.8453 4.676C13.0651 4.45617 13.3198 4.34625 13.6095 4.34625C13.8993 4.34625 14.1542 4.45617 14.374 4.676C14.5938 4.896 14.7038 5.15083 14.7038 5.4405C14.7038 5.73017 14.5938 5.985 14.374 6.205C14.1542 6.42483 13.8993 6.53475 13.6095 6.53475ZM13.6095 9.98475C13.3198 9.98475 13.0651 9.87483 12.8453 9.655C12.6254 9.435 12.5155 9.18017 12.5155 8.8905C12.5155 8.60083 12.6254 8.346 12.8453 8.126C13.0651 7.90617 13.3198 7.79625 13.6095 7.79625C13.8993 7.79625 14.1542 7.90617 14.374 8.126C14.5938 8.346 14.7038 8.60083 14.7038 8.8905C14.7038 9.18017 14.5938 9.435 14.374 9.655C14.1542 9.87483 13.8993 9.98475 13.6095 9.98475Z" fill="#EAA407"/>
    </svg>
  );
}

function MerchantAvatar({
  logoUrl,
  name,
  rounded = "full",
}: {
  logoUrl: string;
  name: string;
  rounded?: "full" | "xl";
}) {
  const [errored, setErrored] = useState(false);
  const initial = (name?.trim()?.[0] ?? "?").toUpperCase();
  const radius = rounded === "xl" ? "rounded-xl" : "rounded-full";

  if (!logoUrl || errored) {
    return (
      <div
        className={`size-11 ${radius} border-[0.5px] border-[#DAE0EB] bg-[#F5F7FB] flex items-center justify-center text-[14px] font-semibold text-[#585B7A] shrink-0`}
        aria-hidden="true"
      >
        {initial}
      </div>
    );
  }

  return (
    <div
      className={`size-11 ${radius} border-[0.5px] border-[#DAE0EB] overflow-hidden shrink-0 bg-white`}
    >
      <img
        src={logoUrl}
        alt=""
        className="size-full object-cover"
        onError={() => setErrored(true)}
      />
    </div>
  );
}

const OVERDUE_COLLAPSED_COUNT = 2;

function OverdueStatusLabel({ daysOverdue }: { daysOverdue?: number }) {
  const { t } = useTranslation();
  if (daysOverdue == null || daysOverdue <= 0) return null;
  const key =
    daysOverdue === 1
      ? "overdue_payments.day_overdue"
      : "overdue_payments.days_overdue";
  return (
    <div className="text-[12px] leading-[16px] font-medium text-[#DA1E28] tracking-[-0.18px]">
      {t(key, { count: daysOverdue })}
    </div>
  );
}

interface RepaymentBodyProps {
  ctx: AppContext;
  flow: RepaymentFlowContext;
  /** False while SWR is still loading saved cards — show same loader as payment-confirmation-new. */
  savedCardsLoaded: boolean;
  paymentMethods: PaymentMethodOption[];
  selectedPaymentMethod: ReturnType<typeof useSelectedPaymentMethod>;
  onMethodSelect: (method: PaymentMethodOption | "new-card") => void;
  showInlineCardForm: boolean;
  cardForm: CardFormState;
  onCardFormChange: (form: CardFormState) => void;
  cardFormError: PageError;
  onCardFormBack: () => void;
}

function RepaymentBody({
  ctx,
  flow,
  savedCardsLoaded,
  paymentMethods,
  selectedPaymentMethod,
  onMethodSelect,
  showInlineCardForm,
  cardForm,
  onCardFormChange,
  cardFormError,
  onCardFormBack,
}: RepaymentBodyProps) {
  const { t, i18n } = useTranslation();
  const { session } = ctx;
  const isOverdue = flow.mode === "overdue";
  const copyPrefix = isOverdue ? "overdue_payments" : "early_repayment";
  const trackingScope = isOverdue ? "overdue-payments" : "default";
  const [overdueExpanded, setOverdueExpanded] = useState(false);

  const earlyRepayment = flow.mode === "early" ? flow.earlyRepayment : null;
  const overduePayments = flow.mode === "overdue" ? flow.overduePayments : null;

  const dueDateLabel = earlyRepayment
    ? t("early_repayment.due_on", {
        date: ctx.displayDate(earlyRepayment.dueDate),
      })
    : "";
  const amount = flow.paymentAmount;
  const currency = flow.currency;

  const visibleOverdueRepayments = useMemo(() => {
    if (!overduePayments) return [];
    const items = overduePayments.payments;
    if (overdueExpanded || items.length <= OVERDUE_COLLAPSED_COUNT) {
      return items;
    }
    return items.slice(0, OVERDUE_COLLAPSED_COUNT);
  }, [overduePayments, overdueExpanded]);

  // payment-methods-view omits onBack when cards[] is empty; only show back
  // when the user had another method to pick (saved card / Apple Pay).
  const hasPaymentMethodsToChoose = paymentMethods.length > 0;

  return (
    <div className="px-5 pt-3 pb-[18px] flex flex-col gap-8">
      {/* Title + alert */}
      <div className="flex flex-col gap-6">
        <h1
          className="text-[24px] leading-[28px] font-bold text-[#16181D] text-center"
          style={{ letterSpacing: "-0.36px" }}
        >
          {t(`${copyPrefix}.title`)}
        </h1>

        <div
          className="flex items-center gap-[14px] p-4 rounded-[20px] border-2 border-[#EAA407] bg-[#FDF6E6]"
          role="status"
        >
          <div className="shrink-0">
            <ReceiptIcon />
          </div>
          <p
            className="text-[12px] leading-[1.6] font-medium text-[#585B7A]"
            style={{ letterSpacing: "-0.18px" }}
          >
            {t(`${copyPrefix}.alert_body`)}
          </p>
        </div>
      </div>

      {isOverdue && overduePayments ? (
        <section
          className="flex flex-col gap-4 border border-[#EBEEF4] rounded-2xl px-3 py-4"
          data-testid="overdue-payments-section"
        >
          <div className="flex flex-col gap-3 w-full">
            <p
              className="text-[14px] leading-[20px] font-semibold text-[#16181D]"
              style={{ letterSpacing: "-0.21px" }}
            >
              <span>{t("overdue_payments.total")}: </span>
              <MoneyAmount
                amount={overduePayments.totalDueAmount.amount}
                currency={overduePayments.totalDueAmount.currency}
                language={i18n.language}
                className="inline-flex align-baseline text-[14px] leading-[20px] font-semibold text-[#16181D]"
              />
            </p>
            <div className="h-px w-full bg-[#EBEEF4]" aria-hidden />
          </div>

          <div className="flex flex-col gap-5 w-full">
            {visibleOverdueRepayments.map((repayment) => (
              <div
                key={
                  repayment.paymentId ||
                  `${repayment.orderId}-${repayment.installmentIndex}`
                }
                className="flex items-center gap-3"
                data-testid="repayment-installment-row"
              >
                <MerchantAvatar
                  logoUrl={repayment.merchantLogoUrl}
                  name={repayment.merchantName}
                  rounded="xl"
                />
                <div className="flex flex-1 items-center gap-4 min-w-0">
                  <div className="flex flex-1 flex-col gap-0.5 min-w-0">
                    <div
                      className="text-[14px] leading-[20px] font-semibold text-[#16181D] truncate"
                      style={{ letterSpacing: "-0.21px" }}
                    >
                      {repayment.merchantName}
                    </div>
                    <OverdueStatusLabel
                      daysOverdue={resolveRepaymentDaysOverdue(
                        repayment,
                        overduePayments.maxDaysOverdue,
                      )}
                    />
                  </div>
                  <MoneyAmount
                    amount={repayment.amount.amount}
                    currency={repayment.amount.currency}
                    language={i18n.language}
                    className="inline-flex text-[14px] leading-[20px] font-semibold text-[#16181D] shrink-0"
                  />
                </div>
              </div>
            ))}
          </div>

          {overduePayments.payments.length > OVERDUE_COLLAPSED_COUNT && (
            <div className="flex flex-col gap-3 w-full">
              <div className="h-px w-full bg-[#EBEEF4]" aria-hidden />
              <button
                type="button"
                className="flex items-center justify-center gap-1 w-full text-[12px] leading-[16px] font-semibold text-[#9600F1] tracking-[-0.18px]"
                onClick={() => setOverdueExpanded((value) => !value)}
                data-testid="overdue-payments-show-all"
              >
                {overdueExpanded
                  ? t("overdue_payments.show_less")
                  : t("overdue_payments.show_all")}
                {overdueExpanded ? (
                  <ChevronUp size={16} aria-hidden />
                ) : (
                  <ChevronDown size={16} aria-hidden />
                )}
              </button>
            </div>
          )}
        </section>
      ) : earlyRepayment ? (
        <section className="flex flex-col gap-4" data-testid="early-repayment-section">
          <h2
            className="text-[16px] leading-[26px] font-semibold text-[#16181D]"
            style={{ letterSpacing: "-0.24px" }}
          >
            {t("early_repayment.repayments_heading")}
          </h2>

          <div className="flex items-center gap-3">
            <MerchantAvatar
              logoUrl={earlyRepayment.merchantLogoUrl}
              name={earlyRepayment.merchantName}
            />
            <div className="flex flex-1 items-center gap-4 rounded-lg">
              <div className="flex flex-1 flex-col gap-1.5 min-w-0">
                <div
                  className="text-[14px] leading-[24px] font-semibold text-[#16181D] truncate"
                  style={{ letterSpacing: "-0.21px" }}
                >
                  {earlyRepayment.merchantName}
                </div>
                <div
                  className="text-[12px] leading-[20px] font-medium text-[#585B7A] truncate"
                  style={{ letterSpacing: "-0.18px" }}
                >
                  {t("early_repayment.order_reference", {
                    reference: earlyRepayment.orderReference,
                  })}
                </div>
              </div>
              <div className="flex flex-col gap-1.5 items-end">
                <MoneyAmount
                  amount={amount}
                  currency={currency}
                  language={i18n.language}
                  className="inline-flex text-[14px] leading-[24px] font-semibold text-[#16181D]"
                />
                <div
                  className="text-[12px] leading-[20px] font-medium text-[#585B7A] whitespace-nowrap"
                  style={{ letterSpacing: "-0.18px" }}
                >
                  {dueDateLabel}
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-[#EBEEF4]" />

          <div className="flex items-center gap-4">
            <div
              className="flex-1 text-[16px] leading-[26px] font-medium text-[#16181D]"
              style={{ letterSpacing: "-0.24px" }}
            >
              {t("early_repayment.total")}
            </div>
            <MoneyAmount
              amount={amount}
              currency={currency}
              language={i18n.language}
              className="inline-flex text-[16px] leading-[26px] font-semibold text-[#16181D]"
            />
          </div>
        </section>
      ) : null}

      {/* Confirm and pay section — picker OR inline new-card form */}
      <section className="flex flex-col gap-5">
        <h2
          className="text-[16px] leading-[26px] font-semibold text-[#16181D]"
          style={{ letterSpacing: "-0.24px" }}
        >
          {t("early_repayment.confirm_and_pay")}
        </h2>

        {showInlineCardForm ? (
          <InlineCardForm
            cardForm={cardForm}
            onCardFormChange={onCardFormChange}
            error={cardFormError}
            session={session}
            rtl={ctx.rtl}
            onBack={
              hasPaymentMethodsToChoose ? onCardFormBack : undefined
            }
          />
        ) : !savedCardsLoaded ? (
          <div
            className="border border-gray-200 rounded-[20px] p-6 flex flex-col items-center justify-center gap-4 min-h-[120px]"
            data-testid="payment-methods-loading"
          >
            <CircularProgress size={32} style={{ color: "#9600F1" }} />
            <p className="text-sm text-[#585B7A]">
              {t("payment_confirmation.loading_payment_methods")}
            </p>
          </div>
        ) : (
          <PaymentMethodList
            paymentMethods={paymentMethods}
            selectedMethod={selectedPaymentMethod}
            onMethodSelect={onMethodSelect}
            rtl={ctx.rtl}
            session={session}
            trackingScope={trackingScope}
          />
        )}
      </section>
    </div>
  );
}

export default function EarlyRepaymentScreen({
  onSessionFetched,
}: EarlyRepaymentScreenProps) {
  const { t } = useTranslation();
  const location = useLocation();

  // Mirror the same shouldShowFailureLoading detection used by
  // payment-confirmation-new.tsx so 3DS failure reporting can run if the
  // user is bounced back to /early-repayment after a failed 3DS challenge.
  const shouldShowFailureLoading = useMemo(() => {
    const pathname = location.pathname;
    const searchParams = new URLSearchParams(location.search);
    const result = searchParams.get("result");
    const is3dsFailure = pathname.includes("3ds") && result === "failed";
    const isPaymentMethodFailure =
      pathname.includes("instalment/add-payment-method") &&
      pathname.includes("failed");
    return is3dsFailure || isPaymentMethodFailure;
  }, [location.pathname, location.search]);

  // Source-of-truth session for the screen.
  const { data: session, mutate: mutateSession } = useCheckoutSessionV3({
    skip: shouldShowFailureLoading,
  });

  const repaymentFlow = useMemo(
    () => (session ? getRepaymentFlowContext(session) : null),
    [session],
  );
  const isOverdueFlow = session ? isOverdueRepaymentFlow(session) : false;
  const hasEmittedOverduePageViewRef = useRef(false);

  useEffect(() => {
    if (session?.id && repaymentFlow?.mode === "overdue") {
      latchOverdueRepaymentFlow(session.id);
    }
  }, [session?.id, repaymentFlow?.mode]);

  useEffect(() => {
    if (!session || !isOverdueFlow || hasEmittedOverduePageViewRef.current) {
      return;
    }
    emitOverduePaymentsTracking(
      "payment_overdues__page_viewed",
      session,
      overduePaymentsTotalPayload(session),
    );
    hasEmittedOverduePageViewRef.current = true;
  }, [session, isOverdueFlow]);

  const hasEmittedOverdueSuccessRef = useRef(false);
  useEffect(() => {
    if (
      !session ||
      session.status !== CheckoutStatus.EARLY_REPAYMENT_COMPLETED ||
      !isOverdueFlow ||
      hasEmittedOverdueSuccessRef.current
    ) {
      return;
    }
    emitOverduePaymentsTracking(
      "payment_overdues__process_successful",
      session,
      overduePaymentsTotalPayload(session),
    );
    hasEmittedOverdueSuccessRef.current = true;
  }, [session, isOverdueFlow]);

  const hasPaymentErrorForTracking = useMemo(() => {
    if (!session) return false;
    const [hasError] = parsePaymentResultLib(
      session.paymentResult,
      getPaymentErrorMessage,
    );
    return hasError;
  }, [session]);

  const hasEmittedOverdueFailedRef = useRef(false);
  useEffect(() => {
    if (
      !session ||
      !isOverdueFlow ||
      !hasPaymentErrorForTracking ||
      hasEmittedOverdueFailedRef.current
    ) {
      return;
    }
    emitOverduePaymentsTracking(
      "payment_overdues__process_failed",
      session,
      overduePaymentsTotalPayload(session),
    );
    hasEmittedOverdueFailedRef.current = true;
  }, [session, isOverdueFlow, hasPaymentErrorForTracking]);

  // 3DS failure reporting (extracted hook from payment-confirmation-new).
  const isReporting3dsFailure = use3dsFailureReporting({
    shouldShowFailureLoading,
    mutateSession,
    onSessionFetched,
  });

  const ctxRef = useRef<AppContext | null>(null);

  // Reused payment-method state (Zustand) and saved-cards fetch (SWR).
  const selectedPaymentMethod = useSelectedPaymentMethod();
  const { setSelectedPaymentMethod } = usePaymentPlansActions();
  const { data: savedCards } = useSavedCards({
    merchantId: session?.merchant?.id || "",
    orderId: session?.orderId,
    skip: shouldShowFailureLoading,
  });

  // Apple Pay availability — same gate the regular confirmation page uses.
  const canPayWithAppleDefault = useMemo(() => {
    return Boolean(
      session?.country?.applePayAllowed &&
        "ApplePaySession" in window &&
        typeof (window as { ApplePaySession?: unknown }).ApplePaySession !==
          "undefined" &&
        (savedCards?.metaData?.applePayPreference ?? true),
    );
  }, [session, savedCards]);

  const { paymentMethods, paymentMethodsRef, handleSelectPaymentMethod } =
    usePaymentMethods(
      savedCards?.cards ?? [],
      canPayWithAppleDefault,
      selectedPaymentMethod,
    );

  // Submission + polling — exact same hooks /confirmation uses.
  const {
    processing,
    submitWithExistingCard,
    tokenizeAndSubmitCard,
  } = usePaymentSubmission(session, onSessionFetched);

  // Apple Pay needs the early-repayment amount (not the checkout's
  // down-payment), so we call payWithApple directly instead of going
  // through the hook's submitWithApplePay (which uses
  // PaymentMethodUtils.calculateAmountToPay → ctx.selectedPayment().amountToPayNow,
  // which is 0 during early repayment because there's no selected payment option).
  const cosmosClient = useCosmosClient();
  const [applePayProcessing, setApplePayProcessing] = useState(false);

  // Modals hook — cvv modal, apple pay modal state.
  const {
    cvvModalOpen,
    isApplePayModalOpen,
    hasManuallyClosedApplePayModalRef,
    openCvvModal,
    closeCvvModal,
    openApplePayModal,
    closeApplePayModal,
  } = usePaymentModals();

  usePaymentPolling(session, ctxRef, onSessionFetched);

  // Tracking hooks — mirror payment-confirmation-new exactly.
  usePaymentMethodsTracking(session, savedCards);
  useSlowPaymentTracking(session);

  // Local state — mirror payment-confirmation-new (minus PNM/wallet/document).
  const [cvv, setCvv] = useState("");
  const [cardForm, setCardForm] = useState<CardFormState>(EMPTY_CARD_FORM);
  const [error, setError] = useState<PageError>(PageError.NONE);
  const [showInlineCardForm, setShowInlineCardForm] = useState(false);

  // No saved cards available → auto-open inline card form (mirrors).
  useEffect(() => {
    if (
      savedCards !== undefined &&
      paymentMethods.length === 0 &&
      !selectedPaymentMethod &&
      !showInlineCardForm
    ) {
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
    }
  }, [
    savedCards,
    paymentMethods,
    selectedPaymentMethod,
    showInlineCardForm,
    session,
  ]);

  // Card form validation — same rules as payment-confirmation-new.
  const validateCardForm = useCallback((): boolean => {
    const cardNumberDigits = cardForm.cardNumber.replace(/\s/g, "");
    const isCardLengthValid = [15, 16].includes(cardNumberDigits.length);
    const isCardNumberValid =
      isCardLengthValid && /^\d+$/.test(cardNumberDigits);

    const isExpiryDateValid =
      cardForm.expiryDate.length === 5 &&
      /^\d{2}\/\d{2}$/.test(cardForm.expiryDate);

    let hasFutureExpiry = false;
    if (isExpiryDateValid) {
      const [month, year] = cardForm.expiryDate.split("/");
      const expiryMonth = parseInt(month, 10);
      const expiryYear = 2000 + parseInt(year, 10);
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth() + 1;
      if (expiryYear > currentYear) {
        hasFutureExpiry = true;
      } else if (expiryYear === currentYear && expiryMonth >= currentMonth) {
        hasFutureExpiry = true;
      }
    }

    const isCvvValid =
      cardForm.cvv.length >= 3 &&
      cardForm.cvv.length <= 4 &&
      /^\d+$/.test(cardForm.cvv);

    return (
      isCardNumberValid && isExpiryDateValid && hasFutureExpiry && isCvvValid
    );
  }, [cardForm]);

  // Loading flag — same shape as shouldShowLoading in payment-confirmation-new.
  const isProcessingPayment = CHECKOUT_STATUS_PROCESSING_PAYMENT.includes(
    session?.status ?? CheckoutStatus.UNKNOWN,
  );
  // Mirrors payment-confirmation-new.tsx exactly — the fullscreen
  // "Processing your payment" screen should ONLY render when the BE has
  // moved the session into a processing-payment status. Local FE state like
  // `processing === SUBMIT` (Apple Pay sheet open, or card POST in flight)
  // must not trigger it, otherwise the background behind the Apple Pay
  // native sheet flips to the loader before anything is actually processing.
  const shouldShowLoading = isProcessingPayment || isReporting3dsFailure;

  // Shared refetch for recovery — same pattern as usePaymentSubmission.
  const refetchSessionForRecovery = useCallback(async () => {
    const freshData = await cosmosClient.getCheckoutSessionV3();
    return CksCheckoutStateSchema.parse(freshData);
  }, [cosmosClient]);

  // Apple Pay handler ref pattern — mirrors payment-confirmation-new but calls
  // payWithApple directly with the early-repayment amount.
  const applePayClickHandlerRef = useRef<(() => Promise<void>) | null>(null);
  useEffect(() => {
    if (ctxRef.current) {
      applePayClickHandlerRef.current = async () => {
        if (processing !== Processing.NONE || applePayProcessing) return;
        const flow = getRepaymentFlowContext(ctxRef.current!.session);
        if (!flow) return;
        setApplePayProcessing(true);
        try {
          const newSession = await payWithApple(
            cosmosClient,
            ctxRef.current!,
            window,
            false,
            { termsAccepted: true },
            flow.paymentAmount,
          );
          onSessionFetched(newSession);
        } catch (err) {
          // Skip recovery entirely for local non-HTTP errors (e.g. Apple Pay
          // user cancellation / sheet failure). Routing these through
          // handleHttpError would mark the session with an error and trigger
          // ErrorScreen — we want the user to stay on this screen and retry.
          const apiErr = err as { response?: unknown; request?: unknown; session?: CheckoutSession };
          if (apiErr?.response === undefined && apiErr?.request === undefined) {
            // no-op — processing state is reset in the finally block below
          } else {
            const errorSession = apiErr.session;
            if (errorSession) {
              onSessionFetched(errorSession);
            } else {
              try {
                const result = await handleHttpError(err as ApiError, {
                  refetchSession: refetchSessionForRecovery,
                  currentSession: session,
                  endpoint: "select-payment-method",
                  httpMethod: "POST",
                  requestContext: { paymentMethod: "APPLE_PAY", flow: "early-repayment" },
                });
                onSessionFetched(result.session);
              } catch {
                // Recovery itself failed
              }
            }
          }
        } finally {
          setApplePayProcessing(false);
        }
      };
    }
  }, [processing, applePayProcessing, onSessionFetched, session, refetchSessionForRecovery, cosmosClient]);

  // shouldShowApplePayModal logic — mirrors payment-confirmation-new.
  const shouldShowApplePayModalRef = useRef(false);
  useEffect(() => {
    if (
      shouldShowApplePayModalRef.current &&
      !isApplePayModalOpen &&
      !hasManuallyClosedApplePayModalRef.current
    ) {
      openApplePayModal();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isApplePayModalOpen]);

  // handleAddCard — mirrors payment-confirmation-new (minus wallet/documents).
  const handleAddCard = useCallback(async () => {
    if (!session) return;
    try {
      setError(PageError.NONE);
      await tokenizeAndSubmitCard(
        {
          cardNumber: cardForm.cardNumber,
          expiryDate: cardForm.expiryDate,
          cvv: cardForm.cvv,
        },
        { termsAccepted: true },
        session.customerId ?? "",
        (err) => {
          setError(PageError.TOKENIZATION_FAILED);
          throw err;
        },
      );

      setSelectedPaymentMethod({
        type: "card",
        id: "new",
        name: "",
        last4: cardForm.cardNumber.slice(-4),
        icon: "",
      });

      setCardForm(EMPTY_CARD_FORM);
      setShowInlineCardForm(false);
    } catch {
      // Already tracked / surfaced inside tokenizeAndSubmitCard.
    }
  }, [
    session,
    cardForm,
    tokenizeAndSubmitCard,
    setSelectedPaymentMethod,
  ]);

  // handleCvvSubmit — mirrors payment-confirmation-new.
  const handleCvvSubmit = useCallback(async () => {
    if (!session || !selectedPaymentMethod) return;

    const selectedCard = savedCards?.cards?.find(
      (card) => String(card.id) === selectedPaymentMethod.id,
    );

    try {
      await submitWithExistingCard(
        selectedPaymentMethod.id ?? "",
        { termsAccepted: true },
        cvv,
        selectedCard?.fingerprint,
        selectedCard,
      );
      closeCvvModal();
      setCvv("");
    } catch (err) {
      logError(
        "early-repayment-screen",
        "Error processing CVV payment",
        err instanceof Error ? err : new Error(String(err)),
        { error: err },
      );
      closeCvvModal();
      setCvv("");
    }
  }, [
    session,
    selectedPaymentMethod,
    savedCards,
    submitWithExistingCard,
    cvv,
    closeCvvModal,
  ]);

  // handlePayClick — mirrors the branching logic from payment-confirmation-new
  // (minus PNM, wallet-only, document-signing branches that don't apply here).
  // Same re-entry guard as PaymentFooter: blocks double-submit during
  // TOKENIZATION/SUBMIT (payment-confirmation-new CTA is not a custom button).
  const handlePayClick = useCallback(async () => {
    const ctx = ctxRef.current;
    if (!ctx || !session) return;
    if (processing !== Processing.NONE) return;

    const payFlow = getRepaymentFlowContext(ctx.session);
    if (payFlow?.mode === "overdue") {
      emitOverduePaymentsTracking(
        "payment_overdues__settle_pay_button_clicked",
        ctx.session,
        overduePaymentsTotalPayload(ctx.session),
      );
      emitOverduePaymentsTracking(
        "payment_overdues__process_initiated",
        ctx.session,
        overduePaymentsTotalPayload(ctx.session),
      );
    }

    // Apple Pay — call service directly with the early-repayment amount.
    if (selectedPaymentMethod?.type === "apple_pay") {
      const flow = getRepaymentFlowContext(ctx.session);
      if (!flow) return;
      setApplePayProcessing(true);
      try {
        const newSession = await payWithApple(
          cosmosClient,
          ctx,
          window,
          false,
          { termsAccepted: true },
          flow.paymentAmount,
        );
        onSessionFetched(newSession);
      } catch (err) {
        // Skip recovery entirely for local non-HTTP errors (e.g. Apple Pay
        // user cancellation / sheet failure). Routing these through
        // handleHttpError would mark the session with an error and trigger
        // ErrorScreen — we want the user to stay on this screen and retry.
        const apiErr = err as { response?: unknown; request?: unknown; session?: CheckoutSession };
        if (apiErr?.response === undefined && apiErr?.request === undefined) {
          // no-op — processing state is reset in the finally block below
        } else if (apiErr.session) {
          onSessionFetched(apiErr.session);
        } else {
          try {
            const result = await handleHttpError(err as ApiError, {
              refetchSession: refetchSessionForRecovery,
              currentSession: session,
              endpoint: "select-payment-method",
              httpMethod: "POST",
              requestContext: { paymentMethod: "APPLE_PAY", flow: "early-repayment" },
            });
            onSessionFetched(result.session);
          } catch {
            // Recovery itself failed
          }
        }
      } finally {
        setApplePayProcessing(false);
      }
      return;
    }

    // New card form submission (form is open + valid)
    if (
      (!selectedPaymentMethod || selectedPaymentMethod.id === "new") &&
      validateCardForm() &&
      showInlineCardForm
    ) {
      return await handleAddCard();
    }

    // No payment method → open inline card form
    if (!selectedPaymentMethod) {
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
      return;
    }

    // selectedPaymentMethod.id === "new" but form not yet shown
    if (selectedPaymentMethod.id === "new") {
      if (showInlineCardForm) return;
      setShowInlineCardForm(true);
      if (session) {
        emitCardFormTracking("new_card_form_opened", session);
      }
      return;
    }

    // Saved card path — find the matching method, branch on isDisabledCvv
    const paymentMethod = (paymentMethodsRef.current ?? []).find(
      (m) => (m.card?.id ?? 0) + "" === selectedPaymentMethod.id,
    );

    if (!paymentMethod) {
      logError(
        "early-repayment-screen",
        "Could not find payment method object",
        undefined,
        { selectedPaymentMethodId: selectedPaymentMethod.id },
      );
      return;
    }

    if (paymentMethod.card?.isDisabledCvv ?? false) {
      return await submitWithExistingCard(
        selectedPaymentMethod.id,
        { termsAccepted: true },
        undefined,
        paymentMethod.card?.fingerprint,
        paymentMethod.card,
      );
    }

    // Card requires CVV — open the CVV modal.
    ctx.sendEvent(events.CVV_MODAL_SHOWN);
    if (session) {
      emitModalTracking("cvv_modal_opened", session, {
        modal_name: "cvv",
      });
    }
    openCvvModal();
  }, [
    session,
    selectedPaymentMethod,
    processing,
    validateCardForm,
    showInlineCardForm,
    handleAddCard,
    onSessionFetched,
    refetchSessionForRecovery,
    submitWithExistingCard,
    paymentMethodsRef,
    openCvvModal,
    cosmosClient,
  ]);

  // handlePaymentMethodSelect — mirrors payment-confirmation-new.
  const handlePaymentMethodSelect = useCallback(
    (method: PaymentMethodOption | "new-card") => {
      if (method === "new-card") {
        setShowInlineCardForm(true);
        setSelectedPaymentMethod({
          type: "card",
          id: "new",
          name: "",
          last4: "",
          icon: "",
        });
      } else {
        handleSelectPaymentMethod(method);
        setShowInlineCardForm(false);
      }
    },
    [handleSelectPaymentMethod, setSelectedPaymentMethod],
  );

  // handleInlineCardFormBack — mirrors payment-confirmation-new.
  const handleInlineCardFormBack = useCallback(() => {
    setShowInlineCardForm(false);
    if (selectedPaymentMethod?.id === "new") {
      if (paymentMethods.length > 0) {
        handleSelectPaymentMethod(paymentMethods[0]);
      } else {
        setSelectedPaymentMethod(undefined);
      }
    }
  }, [
    selectedPaymentMethod?.id,
    paymentMethods,
    handleSelectPaymentMethod,
    setSelectedPaymentMethod,
  ]);

  // Defensive: clear stale "new" selection on first mount so the picker
  // doesn't open in the empty new-card stub if the previous screen left it.
  useEffect(() => {
    if (selectedPaymentMethod?.id === "new") {
      setSelectedPaymentMethod(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Standalone fullscreen processing screen — same JSX as payment-confirmation-new.
  const loadingPage = useMemo(
    () => (
      <div className="fixed inset-0 bg-gray-100 flex flex-col z-10">
        <div className="w-full md:w-[480px] mx-auto bg-white min-h-screen md:rounded-t-[32px] md:min-h-[calc(100vh-24px)] md:mt-[24px] md:shadow-xs flex flex-col">
          <LoadingPage
            title={t("loading.processing_payment")}
            message={t("loading.do_not_refresh")}
          />
        </div>
      </div>
    ),
    [t],
  );

  // Disabled state for the Confirm and pay button — mirrors PaymentFooter's
  // canSubmitPayment computation (minus wallet branches that don't apply).
  const selectedCardIsNew = selectedPaymentMethod?.id === "new";
  const hasValidCardForm = validateCardForm();
  const canSubmitPayment =
    selectedPaymentMethod !== undefined || hasValidCardForm;
  const buttonDisabled =
    (selectedCardIsNew && !hasValidCardForm) ||
    !canSubmitPayment ||
    processing !== Processing.NONE ||
    applePayProcessing;
  const payButtonLoading =
    processing !== Processing.NONE || applePayProcessing;

  const isEarlyRepaymentCompleted =
    session?.status === CheckoutStatus.EARLY_REPAYMENT_COMPLETED;

  const navigate = useNavigate();
  const [acknowledging, setAcknowledging] = useState(false);

  const handleContinueToCheckout = useCallback(async () => {
    if (acknowledging) return;
    setAcknowledging(true);
    if (session?.id) {
      clearLatchedOverdueRepaymentFlow(session.id);
    }
    try {
      const newSession = await cosmosClient.acknowledgePayment();
      onSessionFetched(newSession);
      navigate(withCheckoutId("/"), { replace: true });
    } catch (err) {
      logError(
        "early-repayment-screen",
        "Failed to acknowledge early-repayment payment",
        err instanceof Error ? err : new Error(String(err)),
        { sessionId: session?.id },
      );
      // Even on failure, try to navigate so the user isn't stuck.
      navigate(withCheckoutId("/"), { replace: true });
    } finally {
      setAcknowledging(false);
    }
  }, [acknowledging, cosmosClient, onSessionFetched, navigate, session?.id]);

  return (
    <AppWrapper
      showLogo
      showCartButton={false}
      showLanguageSwitcher
      disableHeaderInteractions={shouldShowLoading || isEarlyRepaymentCompleted}
      onSessionFetch={(ctx) => onSessionFetched(ctx.session)}
      stickyFooterWithShadow
      stickyFooter={
        shouldShowLoading
          ? undefined
          : isEarlyRepaymentCompleted
            ? (ctx) => (
                <Button
                  variant="primary"
                  size="large"
                  rounded
                  loading={acknowledging}
                  disabled={acknowledging}
                  onClick={() => {
                    void handleContinueToCheckout();
                  }}
                >
                  {ctx.t("early_repayment.continue_to_checkout")}
                </Button>
              )
            : (ctx) => (
                <Button
                  variant="primary"
                  size="large"
                  rounded
                  disabled={buttonDisabled}
                  loading={payButtonLoading}
                  onClick={() => {
                    void handlePayClick();
                  }}
                >
                  {ctx.t("early_repayment.confirm_and_pay")}
                </Button>
              )
      }
    >
      {(ctx) => {
        ctxRef.current = ctx;

        // Success screen — shown when BE has moved to EARLY_REPAYMENT_COMPLETED.
        if (isEarlyRepaymentCompleted) {
          return (
            <div className="flex flex-1 flex-col items-center justify-center px-5 pt-[120px] pb-6">
              <div className="flex flex-col items-center gap-4 w-full">
                <div className="flex items-center justify-center size-[120px] rounded-full bg-[#F0FDF6]">
                  <SuccessFilledIcon />
                </div>
                <h1
                  className="text-[28px] leading-[36px] font-bold text-[#16181D] text-center w-full"
                  style={{ letterSpacing: "-0.42px" }}
                >
                  {t("early_repayment.success_title")}
                </h1>
                <p
                  className="text-[14px] leading-[1.5] font-medium text-[#585B7A] text-center w-full"
                  style={{ letterSpacing: "-0.21px" }}
                >
                  {t(isOverdueFlow ? "overdue_payments.success_body" : "early_repayment.success_body")}
                </p>
              </div>
            </div>
          );
        }

        // Apple Pay modal gating — mirrors payment-confirmation-new.
        const applePayIsSelected =
          selectedPaymentMethod?.type === "apple_pay";
        const shouldShowApplePayModal = applePayIsSelected;
        shouldShowApplePayModalRef.current = shouldShowApplePayModal;

        if (shouldShowLoading) {
          return loadingPage;
        }

        // Payment error detection — mirrors payment-confirmation-new.
        const [hasPaymentError, paymentError] = session
          ? parsePaymentResultLib(
              session.paymentResult,
              getPaymentErrorMessage,
            )
          : [false, ""];

        const flow = getRepaymentFlowContext(ctx.session);
        if (!flow) {
          return <div />;
        }

        return (
          <>
            {hasPaymentError && (
              <div className="px-6 mb-2" data-testid="payment-error-banner">
                <Banner type={BannerType.ERROR}>{paymentError}</Banner>
                <p
                  className="mt-2 text-sm text-[#585B7A]"
                  data-testid="payment-error-recovery-hint"
                >
                  {t("payment_confirmation.error_recovery_hint")}
                </p>
              </div>
            )}

            <RepaymentBody
              ctx={ctx}
              flow={flow}
              savedCardsLoaded={savedCards !== undefined}
              paymentMethods={paymentMethods}
              selectedPaymentMethod={selectedPaymentMethod}
              onMethodSelect={handlePaymentMethodSelect}
              showInlineCardForm={showInlineCardForm}
              cardForm={cardForm}
              onCardFormChange={setCardForm}
              cardFormError={error}
              onCardFormBack={handleInlineCardFormBack}
            />

            {/* CVV modal — same as payment-confirmation-new */}
            <CvvModal
              isOpen={cvvModalOpen}
              onClose={() => {
                closeCvvModal();
                setCvv("");
              }}
              cvv={cvv}
              onCvvChange={setCvv}
              onSubmit={handleCvvSubmit}
              processing={processing}
              session={session}
              selectedPaymentMethodId={selectedPaymentMethod?.id}
              isWalletSelected={false}
              documentSignatureIsPending={false}
              isProcessingPayment={isProcessingPayment}
            />

            {/* Apple Pay modal — same as payment-confirmation-new */}
            {shouldShowApplePayModal && (
              <ApplePayModal
                isOpen={isApplePayModalOpen}
                onClose={closeApplePayModal}
                onPayClick={() => {
                  applePayClickHandlerRef.current?.()?.catch((err) => {
                    logError(
                      "early-repayment-screen",
                      "Error processing Apple Pay",
                      err instanceof Error ? err : new Error(String(err)),
                      { error: err },
                    );
                  });
                }}
                downPaymentAmount={flow.paymentAmount}
                currency={flow.currency || undefined}
              />
            )}
          </>
        );
      }}
    </AppWrapper>
  );
}
